import React, { useEffect, useRef } from 'react';
import { Grid } from '@mui/material';
import { Button, Stack, TextField } from '@curves/ui-base';

import { CompanyDialogBox } from '@Local/ClubCompany/Dialog';
import { PageLayout } from '@Local/Components/Layout';
import { OverviewListLayout, PageSortAndTotalBox, OverviewListToolbar } from '@Local/Components/OverviewList';
import useCompanyApi from '@Apis/useCompanyApi';
import useOverview from '@Util/hook/useOverview';

const defaultSearchParams = {
    pageIndex: 1,
    pageSize: 20,
};

/**
 * 公司管理頁
 */
const CompanyPage = () => {
    const { postCompaniesOverviewApi, getCompanyApi } = useCompanyApi();
    const dialogRef = useRef(null);
    const companyNameRef = useRef(null);
    const taxIDRef = useRef(null);
    const { overviewData, getOverviewData, searchParams } = useOverview({
        getOverviewApi: postCompaniesOverviewApi,
        defaultSearchParams,
        isFetchOnMount: true,
        enableQueryString: true,
    });
    const { list = [], pagination = {} } = overviewData || {};

    // Dialog 非同步完成後仍需沿用使用者最後一次的搜尋條件，因此以 ref 避免 callback 捕捉到舊 state。
    const searchParamsRef = useRef(searchParams);

    useEffect(() => {
        searchParamsRef.current = searchParams;
    }, [searchParams]);

    const handleGetOverviewData = params => {
        // 先同步 ref，讓新增／編輯成功後的 refresh 可立即用同一組條件重查。
        searchParamsRef.current = params;
        return getOverviewData(params);
    };

    const refreshCompanies = () => handleGetOverviewData(searchParamsRef.current);

    const handlePageChange = (e, page) => {
        // 換頁只更新頁碼，保留目前所有搜尋條件。
        handleGetOverviewData({ ...searchParamsRef.current, pageIndex: page });
    };

    const handleSearch = () => {
        const companyName = (companyNameRef.current?.getResult?.() ?? '').trim();
        const taxID = (taxIDRef.current?.getResult?.() ?? '').trim();

        handleGetOverviewData({
            companyName,
            taxID,
            pageIndex: 1,
            pageSize: 20,
        });
    };

    const handleOpenCompanyDialog = async (targetData = null) => {
        let sourceData = targetData;

        if (targetData?.companyID) {
            sourceData = await getCompanyApi(targetData.companyID);
        }

        if (dialogRef.current && dialogRef.current.openCompanyDialog) {
            dialogRef.current.openCompanyDialog(sourceData, refreshCompanies);
        }
    };

    const pageActions = [
        {
            label: '新增公司',
            variant: 'contained',
            onClick: () => handleOpenCompanyDialog(null),
        },
    ];

    const headerRow = [
            { cellKey: 'companyName', headerLabel: '公司名稱' },
            { cellKey: 'taxID', headerLabel: '統編' },
            { cellKey: 'taxTitle', headerLabel: '抬頭' },
            { cellKey: 'companyNumber', headerLabel: '電話' },
            {
                cellKey: 'action',
                headerLabel: '操作',
                align: 'center',
                formatCell: (_, rowData) => (
                    <Stack direction={'row'} spacing={1} justifyContent={'center'}>
                        <Button variant={'outlined'} size={'small'} color="primary" onClick={() => handleOpenCompanyDialog(rowData)}>
                            編輯
                        </Button>
                    </Stack>
                ),
            },
    ];

    return (
        <PageLayout className={'company-container'}>
            <PageLayout.Header title="公司管理" actions={pageActions} />
            <OverviewListLayout>
                <OverviewListLayout.SearchBar isShowActions onSubmit={handleSearch}>
                    <Grid container spacing={2}>
                        <Grid item xs={6}>
                            <TextField
                                key={searchParams?.companyName || ''}
                                ref={companyNameRef}
                                label={'公司名稱'}
                                defaultValue={searchParams?.companyName || ''}
                                InputProps={{ placeholder: '請輸入公司名稱' }}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                key={searchParams?.taxID || ''}
                                ref={taxIDRef}
                                label={'統編'}
                                defaultValue={searchParams?.taxID || ''}
                                InputProps={{ placeholder: '請輸入統一編號' }}
                                fullWidth
                            />
                        </Grid>
                    </Grid>
                </OverviewListLayout.SearchBar>
                <OverviewListToolbar>
                    <PageSortAndTotalBox
                        pageIndex={pagination.pageIndex}
                        pageSize={pagination.pageSize}
                        totalCount={pagination.totalCount}
                        isCurrentPageFirstIndex
                        isCurrentPageLastIndex
                    />
                </OverviewListToolbar>
                <OverviewListLayout.Table
                    headerRow={headerRow}
                    list={list}
                    pageIndex={pagination.pageIndex}
                    totalPage={pagination.totalPage}
                    onChangePage={handlePageChange}
                    isPagination
                />
            </OverviewListLayout>
            <CompanyDialogBox ref={dialogRef} />
        </PageLayout>
    );
};

export default CompanyPage;
